import {
  ApiBlueprint,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';

export type GitLabDeploymentAction =
  | 'deploy'
  | 'rollback'
  | 'current'
  | 'active'
  | 'unavailable';

export interface GitLabEnvironmentSummary {
  environment: string;
  status: string;
  commitSha: string;
  shortCommitSha: string;
  commitTitle?: string;
  commitWebUrl?: string;
  ref: string;
  pipelineId: number;
  pipelineWebUrl: string;
  jobId: number;
  jobWebUrl: string;
  deployedAt: string;
  deployedBy?: string;
}

export interface GitLabDeploymentCandidate {
  id: number;
  pipelineId: number;
  pipelineWebUrl: string;
  sourceJobId: number;
  sourceJobWebUrl: string;
  sourceStatus: string;
  targetJobId?: number;
  targetJobWebUrl?: string;
  targetStatus?: string;
  commitSha: string;
  shortCommitSha: string;
  commitTitle?: string;
  commitWebUrl?: string;
  commitAuthor?: string;
  authoredAt?: string;
  ref: string;
  deployedAt: string;
  action: GitLabDeploymentAction;
}

export interface GitLabDeploymentsPage {
  target: {
    id: string;
    name: string;
    entityRef: string;
    projectPath: string;
    projectWebUrl: string;
    sourceEnvironment: string;
    targetEnvironment: string;
  };
  environments: {
    source?: GitLabEnvironmentSummary;
    target?: GitLabEnvironmentSummary;
  };
  candidates: GitLabDeploymentCandidate[];
  active: boolean;
  nextCursor?: string;
}

export interface GitLabDeploymentRequestResult {
  action: 'deploy' | 'rollback';
  jobId: number;
  status: string;
  webUrl: string;
}

export interface GitLabDeploymentsApi {
  getDeployments(options: {
    targetId: string;
    cursor?: string;
  }): Promise<GitLabDeploymentsPage>;
  requestDeployment(options: {
    targetId: string;
    pipelineId: number;
  }): Promise<GitLabDeploymentRequestResult>;
}

export const gitLabDeploymentsApiRef =
  createApiRef<GitLabDeploymentsApi>().with({
    id: 'plugin.gitlab-jobs.deployments',
  });

async function requireSuccess(response: Response): Promise<void> {
  if (response.ok) {
    return;
  }
  if (response.status === 409) {
    throw new Error('A UAT deployment is already active.');
  }
  if (response.status === 403) {
    throw new Error('You are not permitted to request this deployment.');
  }
  if (response.status === 404) {
    throw new Error(
      'Deployment target unavailable. Check its Catalog mapping and GitLab access.',
    );
  }
  if (response.status === 503) {
    throw new Error('GitLab is unavailable. Try again shortly.');
  }
  throw new Error('Unable to complete the deployment request.');
}

export class GitLabDeploymentsClient implements GitLabDeploymentsApi {
  readonly #discoveryApi: DiscoveryApi;
  readonly #fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.#discoveryApi = options.discoveryApi;
    this.#fetchApi = options.fetchApi;
  }

  async getDeployments(options: {
    targetId: string;
    cursor?: string;
  }): Promise<GitLabDeploymentsPage> {
    const baseUrl = await this.#discoveryApi.getBaseUrl('gitlab-jobs');
    const url = new URL(
      `${baseUrl}/deployments/${encodeURIComponent(options.targetId)}`,
    );
    if (options.cursor) {
      url.searchParams.set('cursor', options.cursor);
    }
    const response = await this.#fetchApi.fetch(url);
    await requireSuccess(response);
    return response.json() as Promise<GitLabDeploymentsPage>;
  }

  async requestDeployment(options: {
    targetId: string;
    pipelineId: number;
  }): Promise<GitLabDeploymentRequestResult> {
    const baseUrl = await this.#discoveryApi.getBaseUrl('gitlab-jobs');
    const url = new URL(
      `${baseUrl}/deployments/${encodeURIComponent(
        options.targetId,
      )}/pipelines/${options.pipelineId}`,
    );
    const response = await this.#fetchApi.fetch(url, { method: 'POST' });
    await requireSuccess(response);
    return response.json() as Promise<GitLabDeploymentRequestResult>;
  }
}

export const gitLabDeploymentsApi = ApiBlueprint.make({
  name: 'deployments',
  params: defineParams =>
    defineParams({
      api: gitLabDeploymentsApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new GitLabDeploymentsClient({ discoveryApi, fetchApi }),
    }),
});
